/**
 * TemplatesPage
 *
 * Main container component for the templates feature using the V2 API.
 * Orchestrates the sidebar, editor, and library views with proper state management.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AutosaveState } from '@components/AutosaveStatus';
import { useDeleteModal } from '@context/modals/DeleteModalContext';
import { useEntitlementStatus } from '@features/entitlements';
import { slugify } from '@features/jotdocs/utils/markdown-utils';
import { useBuildFromTemplateMutation } from '@features/templates-ai-edit';
import { useToolbar } from '@features/toolbar';
import { Box, Drawer, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { LAYOUT_DIMENSIONS } from './constants/layout';
import { useOrgTemplates } from './hooks/useOrgTemplates';
import { useTemplateEditor } from './hooks/useTemplateEditor';
import { useTemplates } from './hooks/useTemplates';
import {
  OrgTemplatesSection,
  SectionLibraryModal,
  TemplateBottomBar,
  TemplateEditor as TemplateEditorComponent,
  TemplateLibraryBrowser,
  TemplatesSidebar,
  TemplateTopBar,
  TemplateWelcomeView,
} from './components';
import { useUpdateTemplateMutation } from './store';
import type { SectionLibraryWithMeta, TemplatesViewMode, TemplateWithMeta } from './types';

export const TemplatesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { showDeleteModal, hideDeleteModal } = useDeleteModal();
  const { hide: hideToolbar } = useToolbar();
  const { templateBuild } = useEventTracking();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams, setSearchParams] = useSearchParams();

  // Selected template ID state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    searchParams.get('id'),
  );

  // View mode state
  const [viewMode, setViewMode] = useState<TemplatesViewMode>(
    searchParams.get('id') ? 'editor' : 'empty',
  );

  // Hide toolbar on templates page
  useEffect(() => {
    hideToolbar();
  }, [hideToolbar]);

  // Handle deep link on mount and clear URL params
  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      // The state is already initialized from searchParams in useState above.
      // We just need to clear the URL parameters so they don't interfere with future navigation.
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // UI state
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [isSectionLibraryOpen, setIsSectionLibraryOpen] = useState(false);
  const [sectionInsertionIndex, setSectionInsertionIndex] = useState<number | null>(null);

  // Core templates state using V2 API
  const {
    templates,
    isLoading,
    selectedTemplate,
    isTemplateLoading,
    libraryTemplates,
    isLibraryLoading,
    createFromScratch,
    forkTemplate,
    addTemplate,
    deleteTemplate,
    removeTemplate,
    reorderTemplates,
    pinTemplate,
    unpinTemplate,
    refetch,
  } = useTemplates({
    selectedTemplateId,
    skipLibrary: viewMode !== 'library',
  });

  // Org templates state
  const { isOrgAdmin, convertToOrgTemplate, deleteOrgTemplate } = useOrgTemplates();

  // Get entitlement status for template creation
  const { isBlocked: isTemplateBlocked, isFeatureEnabled: isEntitlementEnabled } =
    useEntitlementStatus('template_creation');

  // Template update mutation for lock toggle
  const [updateTemplateMutation] = useUpdateTemplateMutation();

  // Build from template mutation for AI editing
  const [buildFromTemplate, { isLoading: isEditWithAILoading }] = useBuildFromTemplateMutation();

  // Template editor state
  const templateEditor = useTemplateEditor({
    template: selectedTemplate,
    onSave: refetch,
    onTemplateForked: newTemplateId => {
      setSelectedTemplateId(newTemplateId);
    },
  });

  // Track previous lastSaved to detect when a save completes
  const prevLastSavedRef = useRef<Date | null>(null);
  const [showSavedStatus, setShowSavedStatus] = useState(false);

  // Detect when lastSaved changes (save completed) and show 'saved' status
  useEffect(() => {
    if (templateEditor.lastSaved && templateEditor.lastSaved !== prevLastSavedRef.current) {
      setShowSavedStatus(true);
      // Reset after brief display
      const timer = setTimeout(() => {
        setShowSavedStatus(false);
      }, 2000);
      prevLastSavedRef.current = templateEditor.lastSaved;
      return () => clearTimeout(timer);
    }
  }, [templateEditor.lastSaved]);

  // Derive autosave status from templateEditor state
  const autosaveStatus: AutosaveState = useMemo(() => {
    if (templateEditor.isSaving) return 'saving';
    if (showSavedStatus) return 'saved';
    return 'idle';
  }, [templateEditor.isSaving, showSavedStatus]);

  // Computed values - get template IDs that are already in user's list
  const excludedTemplateIds = useMemo(() => templates.map(t => t.template.id), [templates]);

  // Handle template selection
  const handleSelectTemplate = useCallback(
    (template: TemplateWithMeta) => {
      setSelectedTemplateId(template.template.id);
      setViewMode('editor');

      // Close sidebar on mobile
      if (isMobile) {
        setIsSidebarOpen(false);
      }
    },
    [isMobile],
  );

  // Handle creating new template from scratch
  const handleCreateFromScratch = useCallback(async () => {
    const templateId = await createFromScratch();
    if (templateId) {
      setSelectedTemplateId(templateId);
      setViewMode('editor');
    }
  }, [createFromScratch]);

  // Handle browsing library
  const handleBrowseLibrary = useCallback(() => {
    setSelectedTemplateId(null);
    setViewMode('library');
  }, []);

  // Handle viewing organization templates
  const handleViewOrganizationTemplates = useCallback(() => {
    setSelectedTemplateId(null);
    setViewMode('organization');
  }, []);

  // Handle adding template from library
  const handleAddFromLibrary = useCallback(
    async (templateId: string) => {
      const success = await addTemplate(templateId);
      if (success) {
        setSelectedTemplateId(templateId);
        setViewMode('editor');
      }
    },
    [addTemplate],
  );

  // Override can_fork when entitlement is blocked
  const effectiveCanFork = useMemo(() => {
    return (
      (selectedTemplate?.permissions.can_fork ?? false) &&
      !(isEntitlementEnabled && isTemplateBlocked)
    );
  }, [selectedTemplate?.permissions.can_fork, isEntitlementEnabled, isTemplateBlocked]);

  // Handle duplicate (copy current template)
  const handleDuplicate = useCallback(async () => {
    if (!selectedTemplate) return;

    const newTemplateId = await forkTemplate(selectedTemplate.template.id);
    if (newTemplateId) {
      setSelectedTemplateId(newTemplateId);
    }
  }, [selectedTemplate, forkTemplate]);

  // Handle delete
  const handleDelete = useCallback(() => {
    if (!selectedTemplate) return;

    const isUserOwned = selectedTemplate.template.owner_scope === 'user';
    const title = isUserOwned ? 'Delete Template?' : 'Remove Template?';
    const message = isUserOwned
      ? 'Are you sure you want to delete this template? This action cannot be undone.'
      : 'Are you sure you want to remove this template from your list?';
    const buttonText = isUserOwned ? 'Delete' : 'Remove';

    showDeleteModal({
      isOpen: true,
      title,
      children: <Typography>{message}</Typography>,
      continueText: buttonText,
      cancelText: 'Cancel',
      onCancel: hideDeleteModal,
      onContinue: () => {
        void (async () => {
          let success = false;
          const { id, owner_scope } = selectedTemplate.template;

          switch (owner_scope) {
            case 'user':
              success = await deleteTemplate(id);
              break;
            case 'org':
              success = await deleteOrgTemplate(id);
              break;
            case 'public':
              success = await removeTemplate(id);
              break;
          }

          if (success) {
            setSelectedTemplateId(null);
            setViewMode('empty');
          }

          hideDeleteModal();
        })();
      },
    });
  }, [
    selectedTemplate,
    showDeleteModal,
    hideDeleteModal,
    deleteTemplate,
    deleteOrgTemplate,
    removeTemplate,
  ]);

  // Handle converting to organization template
  const handleConvertToOrg = useCallback(async () => {
    if (!selectedTemplate) return;

    const success = await convertToOrgTemplate(selectedTemplate.template.id);
    if (success) {
      // Refresh the template data to reflect the new scope
      refetch();
    }
  }, [selectedTemplate, convertToOrgTemplate, refetch]);

  // Handle toggling template lock (allow_user_forks)
  const handleToggleLock = useCallback(async () => {
    if (!selectedTemplate) return;

    const currentAllowForks = selectedTemplate.template.allow_user_forks ?? true;
    const newAllowForks = !currentAllowForks;

    try {
      await updateTemplateMutation({
        templateId: selectedTemplate.template.id,
        updateNoteTemplateRequest: {
          allow_user_forks: newAllowForks,
        },
      }).unwrap();

      enqueueSnackbar(
        newAllowForks
          ? 'Template unlocked - members can now customize'
          : 'Template locked - members cannot customize',
        { variant: 'success' },
      );

      // Refresh the template data to reflect the new lock state
      refetch();
    } catch (error) {
      console.error('Failed to toggle template lock:', error);
      enqueueSnackbar('Failed to update template settings', { variant: 'error' });
    }
  }, [selectedTemplate, updateTemplateMutation, enqueueSnackbar, refetch]);

  // Handle edit with AI - load template into AI builder
  const handleEditWithAI = useCallback(async () => {
    if (!selectedTemplate) return;

    try {
      const result = await buildFromTemplate({
        templateBuildFromTemplateRequest: {
          template_id: selectedTemplate.template.id,
        },
      }).unwrap();

      // Track event
      templateBuild.startedFromTemplate({
        task_id: result.task_id,
        source_template_id: selectedTemplate.template.id,
      });

      // Navigate to template builder with task ID
      navigate(`/templates/build/${result.task_id}`);
    } catch (error: any) {
      console.error('Failed to start AI edit:', error);
      enqueueSnackbar(error?.data?.message || 'Failed to start AI edit. Please try again.', {
        variant: 'error',
      });
    }
  }, [selectedTemplate, buildFromTemplate, templateBuild, navigate, enqueueSnackbar]);

  // Handle opening section library modal with insertion index
  const handleOpenSectionLibrary = useCallback((index: number) => {
    setSectionInsertionIndex(index);
    setIsSectionLibraryOpen(true);
  }, []);

  // Handle adding section from library
  const handleAddSectionFromLibrary = useCallback(
    (librarySection: SectionLibraryWithMeta) => {
      // Create a new section in the template with content from the library section
      const insertionIndex = sectionInsertionIndex ?? templateEditor.sections.length;

      // Generate a unique section ID by slugifying the title
      // Ensure uniqueness by appending a suffix if needed
      const baseId = slugify(librarySection.section.title) || 'section';
      const existingIds = new Set(templateEditor.sections.map(s => s.id));

      let sectionId = baseId;
      let suffix = 1;
      while (existingIds.has(sectionId)) {
        sectionId = `${baseId}-${suffix}`;
        suffix += 1;
      }

      const newSection: Omit<import('./types').SectionWithMeta, 'showPreview'> = {
        id: sectionId,
        title: librarySection.section.title,
        order: insertionIndex,
        content: librarySection.section.content,
        legacyModuleId: librarySection.section.legacy_module_id ?? null,
        exampleOutput: librarySection.section.example_output ?? null,
      };
      templateEditor.addSection(newSection, insertionIndex);
      enqueueSnackbar('Section added to template', { variant: 'success' });
      setSectionInsertionIndex(null);
    },
    [templateEditor, enqueueSnackbar, sectionInsertionIndex],
  );

  // Handle closing section library modal
  const handleCloseSectionLibrary = useCallback(() => {
    setIsSectionLibraryOpen(false);
    setSectionInsertionIndex(null);
  }, []);

  // Render main content based on view mode
  const renderMainContent = () => {
    switch (viewMode) {
      case 'library':
        return (
          <TemplateLibraryBrowser
            templates={libraryTemplates}
            excludedTemplateIds={excludedTemplateIds}
            isLoading={isLibraryLoading}
            onAddTemplate={handleAddFromLibrary}
            onClose={() => setViewMode('empty')}
          />
        );

      case 'organization':
        return (
          <Box sx={{ p: 3 }}>
            <OrgTemplatesSection
              onSelectTemplate={templateId => {
                setSelectedTemplateId(templateId);
                setViewMode('editor');
              }}
            />
          </Box>
        );

      case 'editor':
        return (
          <>
            <TemplateEditorComponent
              templateName={templateEditor.templateName}
              templateDescription={templateEditor.templateDescription}
              sections={templateEditor.sections}
              isLoading={isTemplateLoading}
              canEdit={templateEditor.canEdit}
              canFork={templateEditor.canFork}
              ownerScope={selectedTemplate?.template.owner_scope}
              isLocked={selectedTemplate?.isLocked}
              isTitleEditing={templateEditor.isTitleEditing}
              lastSaved={templateEditor.lastSaved}
              onStartEditingTitle={templateEditor.startEditingTitle}
              onTitleChange={templateEditor.updateTitle}
              onFinishEditingTitle={templateEditor.finishEditingTitle}
              onDescriptionChange={templateEditor.updateDescription}
              onReorderSections={templateEditor.reorderSections}
              onRemoveSection={templateEditor.removeSection}
              onAddSection={templateEditor.addSection}
              onUpdateSectionContent={templateEditor.updateSectionContent}
              onUpdateSectionTitle={templateEditor.updateSectionTitle}
              onBrowseLibrary={handleOpenSectionLibrary}
            />
            <SectionLibraryModal
              isOpen={isSectionLibraryOpen}
              onClose={handleCloseSectionLibrary}
              onAddSection={handleAddSectionFromLibrary}
            />
          </>
        );

      case 'empty':
      default:
        return <TemplateWelcomeView onBrowseLibrary={handleBrowseLibrary} />;
    }
  };

  // Sidebar content
  const sidebarContent = (
    <TemplatesSidebar
      templates={templates}
      selectedTemplateId={selectedTemplateId}
      isLoading={isLoading}
      onSelectTemplate={handleSelectTemplate}
      onCreateFromScratch={handleCreateFromScratch}
      onBrowseLibrary={handleBrowseLibrary}
      onReorderTemplates={reorderTemplates}
      onPinTemplate={pinTemplate}
      onUnpinTemplate={unpinTemplate}
      isOrgAdmin={isOrgAdmin}
      onViewOrganizationTemplates={handleViewOrganizationTemplates}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* Sidebar - Desktop */}
      {!isMobile && (
        <Box
          sx={{
            flexShrink: 0,
            width: LAYOUT_DIMENSIONS.SIDEBAR_WIDTH,
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {sidebarContent}
        </Box>
      )}

      {/* Sidebar - Mobile Drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          PaperProps={{
            sx: { width: LAYOUT_DIMENSIONS.SIDEBAR_WIDTH },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Top Bar */}
        <TemplateTopBar
          hasSelectedTemplate={viewMode === 'editor' && selectedTemplate !== null}
          canDuplicate={effectiveCanFork || (selectedTemplate?.permissions.can_edit ?? false)}
          isSidebarVisible={isSidebarOpen}
          onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
          onDuplicate={handleDuplicate}
          canEditWithAI={(selectedTemplate?.permissions.can_edit ?? false) || effectiveCanFork}
          onEditWithAI={handleEditWithAI}
          isEditWithAILoading={isEditWithAILoading}
          canConvertToOrg={
            isOrgAdmin &&
            selectedTemplate?.template.owner_scope === 'user' &&
            selectedTemplate?.permissions.can_edit === true
          }
          onConvertToOrg={handleConvertToOrg}
          isOrgTemplate={selectedTemplate?.template.owner_scope === 'org'}
          canEditOrgSettings={selectedTemplate?.permissions.can_edit === true && isOrgAdmin}
          isLocked={!(selectedTemplate?.template.allow_user_forks ?? true)}
          onToggleLock={handleToggleLock}
        />

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {renderMainContent()}
        </Box>

        {/* Bottom Bar (only for editable templates or templates that can be copied) */}
        {viewMode === 'editor' &&
          selectedTemplate &&
          (templateEditor.canEdit || templateEditor.canFork) && (
            <TemplateBottomBar
              isSaving={templateEditor.isSaving}
              canDelete={selectedTemplate.permissions.can_delete ?? false}
              autosaveStatus={autosaveStatus}
              isDirty={templateEditor.hasChanges}
              lastSavedAt={templateEditor.lastSaved}
              onSave={templateEditor.save}
              onDelete={handleDelete}
            />
          )}
      </Box>
    </Box>
  );
};
